import mitt from 'mitt';
import { remoteTracks, remoteLocation, clientId, client, errorMessage, microphoneEnabled, otherMicrophoneEnabled, selectedCameraId, flipHorizontal, flipVertical, } from '../store';
import logger from '../utils/logger';
import { reload } from '../utils/browser';
import { setClientStatus } from './client-service';
const MAX_RECONNECT_ATTEMPTS = 8;
const ICE_INTERRUPT_RECONNECT_MS = 20000;
const JWT_REFRESH_MARGIN_MS = 5 * 60 * 1000;
const JWT_REFRESH_MIN_DELAY_MS = 30 * 1000;
function jwtExpiryMs(token) {
    try {
        const payloadPart = token.split('.')[1];
        if (!payloadPart)
            return null;
        const padded = payloadPart.replace(/-/g, '+').replace(/_/g, '/');
        const payload = JSON.parse(atob(padded));
        if (typeof payload.exp !== 'number')
            return null;
        return payload.exp * 1000;
    }
    catch (_a) {
        return null;
    }
}
class JitsiService {
    constructor() {
        this.domain = '8x8.vc';
        this.connection = null;
        this.room = null;
        this.client = null;
        this.clientId = null;
        this.localTracks = [];
        this.otherLocalTracks = [];
        this.remoteTracks = {};
        this.tenant = 'vpaas-magic-cookie-763b4f8ff00547719391b98bfe26181c';
        this.cameraDeviceId = null;
        this.flipH = false;
        this.flipV = false;
        this.token = null;
        this.jitsiInited = false;
        this.signalingConnected = false;
        this.conferenceJoined = false;
        this.intentionalDisconnect = false;
        this.reconnecting = false;
        this.reconnectQueuedReason = null;
        this.reconnectAttempt = 0;
        this.reconnectTimer = null;
        this.jwtRefreshTimer = null;
        this.iceWatchdog = null;
        this.networkListenersBound = false;
        this.events = mitt();
        this.onConnectionSuccess = this.onConnectionSuccess.bind(this);
        this.onConnectionFailed = this.onConnectionFailed.bind(this);
        this.onConnectionDisconnected = this.onConnectionDisconnected.bind(this);
        this.onConnectionTokenExpired = this.onConnectionTokenExpired.bind(this);
        this.onConferenceJoined = this.onConferenceJoined.bind(this);
        this.onConferenceLeft = this.onConferenceLeft.bind(this);
        this.onRemoteTrackAdded = this.onRemoteTrackAdded.bind(this);
        this.onRemoteTrackRemoved = this.onRemoteTrackRemoved.bind(this);
        this.onTrackMuteChanged = this.onTrackMuteChanged.bind(this);
        this.onUserJoined = this.onUserJoined.bind(this);
        this.onUserLeft = this.onUserLeft.bind(this);
        this.onConferenceFailed = this.onConferenceFailed.bind(this);
        this.onConferenceError = this.onConferenceError.bind(this);
        this.onTrackStreamingStatusChanged = this.onTrackStreamingStatusChanged.bind(this);
        this.onMediaConnectionInterrupted = this.onMediaConnectionInterrupted.bind(this);
        this.onMediaConnectionRestored = this.onMediaConnectionRestored.bind(this);
        this.onRingStart = this.onRingStart.bind(this);
        this.onRingStop = this.onRingStop.bind(this);
        this.onBrowserOnline = this.onBrowserOnline.bind(this);
        this.subscribeState();
    }
    async connect() {
        if (!this.connection) {
            logger.warn('connect() called before initConnection');
            return;
        }
        this.connection.connect();
    }
    disconnect() {
        void this.teardown({ disposeLocalTracks: true });
    }
    sendRingStartCommand() {
        var _a;
        (_a = this.room) === null || _a === void 0 ? void 0 : _a.sendCommandOnce('ring_start', { attributes: { clientId: this.clientId } });
    }
    sendRingStopCommand() {
        var _a;
        (_a = this.room) === null || _a === void 0 ? void 0 : _a.sendCommandOnce('ring_stop', { attributes: { clientId: this.clientId } });
    }
    subscribeState() {
        client.subscribe((c) => (this.client = c));
        clientId.subscribe((id) => (this.clientId = id));
        remoteTracks.subscribe((tracks) => (this.remoteTracks = tracks));
        microphoneEnabled.subscribe((enabled) => this.toggleLocalAudio(!enabled));
        selectedCameraId.subscribe((id) => {
            const prev = this.cameraDeviceId;
            this.cameraDeviceId = id;
            if (prev && id && prev !== id && this.room) {
                this.switchCamera(id);
            }
        });
        flipHorizontal.subscribe((v) => {
            this.flipH = v;
            this.updateFlipEffect();
        });
        flipVertical.subscribe((v) => {
            this.flipV = v;
            this.updateFlipEffect();
        });
    }
    getRoomName() {
        var _a;
        return (((_a = this.client) === null || _a === void 0 ? void 0 : _a.roomId) || '').toLowerCase();
    }
    async initConnection() {
        const roomName = this.getRoomName();
        if (!roomName || !this.clientId) {
            logger.error('Cannot init Jitsi connection without roomId/clientId', new Error('missing identity'));
            return;
        }
        logger.info('Initialising Jitsi connection');
        this.bindNetworkListeners();
        this.connectionOptions = {
            hosts: {
                domain: this.domain,
                muc: `conference.${this.tenant}.${this.domain}`,
                focus: `focus.${this.domain}`,
            },
            serviceUrl: `wss://${this.domain}/${this.tenant}/xmpp-websocket?room=${roomName}`,
            websocketKeepAliveUrl: `https://${this.domain}/${this.tenant}/_unlock?room=${roomName}`,
            enableWebsocketResume: true,
            // Two unattended office clients behind NAT: stay on JVB. P2P ICE
            // restarts need a live XMPP session, which is exactly what drops here.
            p2p: {
                enabled: false,
            },
            bridgeChannel: {
                preferSctp: true,
            },
            // Current 8x8/JaaS bridges only forward remote video when the client
            // opts into multi-stream / source-name signalling. Without these flags
            // the bridge sends audio but no video (blank remote screen).
            flags: {
                sourceNameSignaling: true,
                sendMultipleVideoStreams: true,
                receiveMultipleVideoStreams: true,
            },
            siteId: this.tenant,
            channelLastN: 25,
            confID: `https://${this.domain}/${this.tenant}/${roomName}`,
            siteID: this.tenant,
        };
        if (!this.jitsiInited) {
            JitsiMeetJS.init({
                disableAP: false,
                disableAEC: false,
                disableNS: false,
                disableAGC: false,
                disableHPF: false,
                disableResponsiveTiles: true,
                disableAudioLevels: false,
                maxFullResolutionParticipants: 0,
            });
            this.jitsiInited = true;
        }
        const token = await this.fetchJaasToken();
        if (!token) {
            this.scheduleReconnect('jaas auth failed');
            return;
        }
        this.token = token;
        this.connectionOptions.websocketKeepAliveUrl = `${this.connectionOptions.websocketKeepAliveUrl}&token=${encodeURIComponent(token)}`;
        this.connection = new JitsiMeetJS.JitsiConnection(null, token, this.connectionOptions);
        this.addConnectionListeners();
        this.scheduleJwtRefresh();
    }
    async fetchJaasToken() {
        var _a;
        try {
            const authResponse = await fetch('/api/jaasAuthenticate', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    user: `${this.clientId}@${this.domain}`,
                    password: (_a = this.client) === null || _a === void 0 ? void 0 : _a.roomId,
                }),
            });
            if (!authResponse.ok) {
                logger.error('Failed to authenticate with JaaS: ' + JSON.stringify({ status: authResponse.status }), new Error('jaas authenticate failed'));
                return null;
            }
            const { token } = await authResponse.json();
            if (!token) {
                logger.error('JaaS auth response missing token', new Error('missing token'));
                return null;
            }
            return token;
        }
        catch (error) {
            logger.error('Failed to authenticate with JaaS', error);
            return null;
        }
    }
    addConnectionListeners() {
        if (!this.connection)
            return;
        this.connection.addEventListener(JitsiMeetJS.events.connection.CONNECTION_ESTABLISHED, this.onConnectionSuccess);
        this.connection.addEventListener(JitsiMeetJS.events.connection.CONNECTION_FAILED, this.onConnectionFailed);
        this.connection.addEventListener(JitsiMeetJS.events.connection.CONNECTION_DISCONNECTED, this.onConnectionDisconnected);
        const tokenExpiredEvent = JitsiMeetJS.events.connection.CONNECTION_TOKEN_EXPIRED;
        if (tokenExpiredEvent) {
            this.connection.addEventListener(tokenExpiredEvent, this.onConnectionTokenExpired);
        }
    }
    removeConnectionListeners() {
        if (!this.connection)
            return;
        this.connection.removeEventListener(JitsiMeetJS.events.connection.CONNECTION_ESTABLISHED, this.onConnectionSuccess);
        this.connection.removeEventListener(JitsiMeetJS.events.connection.CONNECTION_FAILED, this.onConnectionFailed);
        this.connection.removeEventListener(JitsiMeetJS.events.connection.CONNECTION_DISCONNECTED, this.onConnectionDisconnected);
        const tokenExpiredEvent = JitsiMeetJS.events.connection.CONNECTION_TOKEN_EXPIRED;
        if (tokenExpiredEvent) {
            this.connection.removeEventListener(tokenExpiredEvent, this.onConnectionTokenExpired);
        }
    }
    async onConnectionSuccess() {
        this.signalingConnected = true;
        this.reconnectAttempt = 0;
        this.reconnecting = false;
        this.clearReconnectTimer();
        // Stream resume re-fires CONNECTION_ESTABLISHED; keep the existing room.
        if (this.room) {
            logger.info('XMPP connection restored with existing conference');
            this.updateReceiverConstraints();
            this.events.emit('connection_restored');
            return;
        }
        this.room = this.connection.initJitsiConference(this.getRoomName(), this.connectionOptions);
        this.room.setDisplayName(this.client.location);
        for (let i = 0; i < this.otherLocalTracks.length; i++) {
            this.room.addTrack(this.otherLocalTracks[i]);
        }
        this.addRoomListeners();
        this.room.join();
        setTimeout(() => {
            var _a, _b;
            (_a = this.room) === null || _a === void 0 ? void 0 : _a.addCommandListener('ring_start', this.onRingStart);
            (_b = this.room) === null || _b === void 0 ? void 0 : _b.addCommandListener('ring_stop', this.onRingStop);
        }, 5000);
        await setClientStatus(this.clientId, 'in-room');
    }
    addRoomListeners() {
        if (!this.room)
            return;
        this.room.on(JitsiMeetJS.events.conference.CONFERENCE_JOINED, this.onConferenceJoined);
        this.room.on(JitsiMeetJS.events.conference.CONFERENCE_LEFT, this.onConferenceLeft);
        this.room.on(JitsiMeetJS.events.conference.TRACK_ADDED, this.onRemoteTrackAdded);
        this.room.on(JitsiMeetJS.events.conference.TRACK_REMOVED, this.onRemoteTrackRemoved);
        this.room.on(JitsiMeetJS.events.conference.TRACK_MUTE_CHANGED, this.onTrackMuteChanged);
        this.room.on(JitsiMeetJS.events.conference.USER_JOINED, this.onUserJoined);
        this.room.on(JitsiMeetJS.events.conference.USER_LEFT, this.onUserLeft);
        this.room.on(JitsiMeetJS.events.conference.CONFERENCE_FAILED, this.onConferenceFailed);
        this.room.on(JitsiMeetJS.events.conference.CONFERENCE_ERROR, this.onConferenceError);
        this.room.on(JitsiMeetJS.events.conference.TRACK_STREAMING_STATUS_CHANGED, this.onTrackStreamingStatusChanged);
        this.room.on(JitsiMeetJS.events.conference.CONNECTION_INTERRUPTED, this.onMediaConnectionInterrupted);
        this.room.on(JitsiMeetJS.events.conference.CONNECTION_RESTORED, this.onMediaConnectionRestored);
    }
    removeRoomListeners() {
        var _a;
        if (!((_a = this.room) === null || _a === void 0 ? void 0 : _a.off))
            return;
        this.room.off(JitsiMeetJS.events.conference.CONFERENCE_JOINED, this.onConferenceJoined);
        this.room.off(JitsiMeetJS.events.conference.CONFERENCE_LEFT, this.onConferenceLeft);
        this.room.off(JitsiMeetJS.events.conference.TRACK_ADDED, this.onRemoteTrackAdded);
        this.room.off(JitsiMeetJS.events.conference.TRACK_REMOVED, this.onRemoteTrackRemoved);
        this.room.off(JitsiMeetJS.events.conference.TRACK_MUTE_CHANGED, this.onTrackMuteChanged);
        this.room.off(JitsiMeetJS.events.conference.USER_JOINED, this.onUserJoined);
        this.room.off(JitsiMeetJS.events.conference.USER_LEFT, this.onUserLeft);
        this.room.off(JitsiMeetJS.events.conference.CONFERENCE_FAILED, this.onConferenceFailed);
        this.room.off(JitsiMeetJS.events.conference.CONFERENCE_ERROR, this.onConferenceError);
        this.room.off(JitsiMeetJS.events.conference.TRACK_STREAMING_STATUS_CHANGED, this.onTrackStreamingStatusChanged);
        this.room.off(JitsiMeetJS.events.conference.CONNECTION_INTERRUPTED, this.onMediaConnectionInterrupted);
        this.room.off(JitsiMeetJS.events.conference.CONNECTION_RESTORED, this.onMediaConnectionRestored);
    }
    onConnectionFailed(error, ...rest) {
        this.signalingConnected = false;
        const message = typeof error === 'string' ? error : (error === null || error === void 0 ? void 0 : error.message) || 'connection failed';
        logger.error(`Connection failed: ${message} ${(rest === null || rest === void 0 ? void 0 : rest.length) ? JSON.stringify(rest) : ''}`, new Error(message));
        if (this.intentionalDisconnect) {
            return;
        }
        this.scheduleReconnect(message);
    }
    onConnectionDisconnected(msg) {
        this.signalingConnected = false;
        if (this.intentionalDisconnect) {
            logger.info('Disconnected from room', this.room);
            return;
        }
        logger.error('XMPP connection disconnected unexpectedly', new Error(msg || 'disconnected'));
        this.scheduleReconnect(msg || 'connection disconnected');
    }
    onConnectionTokenExpired() {
        logger.warn('Jitsi JWT expired, refreshing');
        void this.refreshJwt('token expired event');
    }
    async onConferenceJoined() {
        this.conferenceJoined = true;
        logger.info('Conference joined, creating local tracks');
        // Tell the bridge which remote video we want. With source-name signalling
        // the JVB will not forward any video until it receives receiver constraints.
        this.updateReceiverConstraints();
        const liveLocalTracks = this.localTracks.filter((t) => {
            var _a, _b;
            const media = (_b = (_a = t.track) === null || _a === void 0 ? void 0 : _a.getTrack) === null || _b === void 0 ? void 0 : _b.call(_a);
            return (media === null || media === void 0 ? void 0 : media.readyState) === 'live';
        });
        if (liveLocalTracks.length > 0) {
            logger.info('Re-adding existing local tracks after reconnect');
            await this.addLocalTracksToRoom(liveLocalTracks.map((t) => t.track));
            return;
        }
        const cameraOpts = this.cameraDeviceId ? { cameraDeviceId: this.cameraDeviceId } : {};
        try {
            const tracks = await JitsiMeetJS.createLocalTracks(Object.assign(Object.assign(Object.assign({}, this.connectionOptions), cameraOpts), { devices: ['video', 'audio'] }));
            this.onLocalTracks(tracks);
        }
        catch (error) {
            logger.error('Failed to create local tracks for audio and/or video. Retrying with video only...', error);
            try {
                const tracks = await JitsiMeetJS.createLocalTracks(Object.assign(Object.assign(Object.assign({}, this.connectionOptions), cameraOpts), { devices: ['video'] }));
                this.onLocalTracks(tracks);
            }
            catch (retryError) {
                logger.error('Failed to create local tracks for video.', retryError);
                errorMessage.set('Er is iets misgegaan met het opvragen van je camera en/of microfoon. Controleer of deze goed zijn ingesteld en aangesloten.');
            }
        }
    }
    onConferenceLeft() {
        this.conferenceJoined = false;
        if (this.intentionalDisconnect || this.reconnecting) {
            return;
        }
        logger.error('Conference left unexpectedly', new Error('conference left'));
        this.scheduleReconnect('conference left');
    }
    updateReceiverConstraints() {
        var _a;
        if (typeof ((_a = this.room) === null || _a === void 0 ? void 0 : _a.setReceiverConstraints) !== 'function') {
            logger.warn('setReceiverConstraints is not available on this lib-jitsi-meet build');
            return;
        }
        try {
            this.room.setReceiverConstraints({
                lastN: 25,
                selectedSources: [],
                onStageSources: [],
                defaultConstraints: { maxHeight: 720 },
                constraints: {},
            });
            logger.info('Receiver video constraints set (defaultConstraints maxHeight 720)');
        }
        catch (error) {
            logger.error('Failed to set receiver constraints', error);
        }
    }
    onLocalTracks(tracks) {
        this.localTracks = tracks.map((track) => ({ track, participantId: 'local' }));
        void this.addLocalTracksToRoom(tracks);
        this.toggleLocalAudio(true);
        void this.updateFlipEffect();
    }
    async addLocalTracksToRoom(tracks) {
        if (!this.room)
            return;
        for (let i = 0; i < tracks.length; i++) {
            const track = tracks[i];
            logger.info('Adding local track', track);
            try {
                await this.room.addTrack(track);
                logger.info('Local tracks added to room');
            }
            catch (error) {
                logger.error('Failed to add local track to room', error);
            }
        }
    }
    async updateFlipEffect() {
        var _a;
        const videoLocal = this.localTracks.find((t) => { var _a, _b; return ((_b = (_a = t.track).getType) === null || _b === void 0 ? void 0 : _b.call(_a)) === 'video'; });
        if (!((_a = videoLocal === null || videoLocal === void 0 ? void 0 : videoLocal.track) === null || _a === void 0 ? void 0 : _a.setEffect))
            return;
        try {
            if (this.flipH || this.flipV) {
                await videoLocal.track.setEffect(this.createFlipEffect());
            }
            else {
                await videoLocal.track.setEffect(undefined);
            }
        }
        catch (error) {
            logger.error('Failed to apply flip effect', error);
        }
    }
    createFlipEffect() {
        const flipH = this.flipH;
        const flipV = this.flipV;
        let canvas;
        let ctx;
        let video;
        let animId;
        return {
            isEnabled: () => true,
            startEffect: (stream) => {
                const sourceTrack = stream.getVideoTracks()[0];
                const settings = sourceTrack.getSettings();
                canvas = document.createElement('canvas');
                canvas.width = settings.width || 1280;
                canvas.height = settings.height || 720;
                ctx = canvas.getContext('2d');
                video = document.createElement('video');
                video.autoplay = true;
                video.muted = true;
                video.srcObject = stream;
                video.play();
                const draw = () => {
                    ctx.save();
                    ctx.translate(flipH ? canvas.width : 0, flipV ? canvas.height : 0);
                    ctx.scale(flipH ? -1 : 1, flipV ? -1 : 1);
                    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
                    ctx.restore();
                    animId = requestAnimationFrame(draw);
                };
                draw();
                return canvas.captureStream(30);
            },
            stopEffect: () => {
                cancelAnimationFrame(animId);
                video === null || video === void 0 ? void 0 : video.remove();
            },
        };
    }
    async switchCamera(deviceId) {
        logger.info(`Switching camera to ${deviceId}`);
        try {
            const [newVideoTrack] = await JitsiMeetJS.createLocalTracks(Object.assign(Object.assign({}, this.connectionOptions), { devices: ['video'], cameraDeviceId: deviceId }));
            const oldVideo = this.localTracks.find((t) => t.track.getType() === 'video');
            if (oldVideo) {
                if (this.room) {
                    await this.room.replaceTrack(oldVideo.track, newVideoTrack);
                }
                oldVideo.track.dispose();
                oldVideo.track = newVideoTrack;
            }
            else {
                this.localTracks.push({ track: newVideoTrack, participantId: 'local' });
                if (this.room) {
                    await this.room.addTrack(newVideoTrack);
                }
            }
            logger.info('Camera switched successfully');
        }
        catch (error) {
            logger.error('Failed to switch camera', error);
        }
    }
    toggleLocalAudio(mute) {
        this.localTracks.forEach((local) => {
            if (local.track.type === 'audio') {
                mute ? local.track.mute() : local.track.unmute();
            }
        });
    }
    onRemoteTrackAdded(track) {
        if (track.isLocal()) {
            return;
        }
        const type = track.getType();
        const participantId = track.getParticipantId();
        logger.info(`Remote track added of type ${type} from participant ${participantId}`);
        const newRemoteTracks = Object.assign({}, this.remoteTracks);
        if (!newRemoteTracks[participantId]) {
            newRemoteTracks[participantId] = {};
        }
        newRemoteTracks[participantId][type] = { track, participantId };
        remoteTracks.set(newRemoteTracks);
        otherMicrophoneEnabled.set(!track.muted);
        this.events.emit('remote_track_added', { type, participantId });
    }
    onRemoteTrackRemoved(track) {
        var _a, _b, _c;
        if ((_a = track === null || track === void 0 ? void 0 : track.isLocal) === null || _a === void 0 ? void 0 : _a.call(track)) {
            return;
        }
        const type = (_b = track === null || track === void 0 ? void 0 : track.getType) === null || _b === void 0 ? void 0 : _b.call(track);
        const participantId = (_c = track === null || track === void 0 ? void 0 : track.getParticipantId) === null || _c === void 0 ? void 0 : _c.call(track);
        logger.info(`Remote track removed of type ${type} from participant ${participantId}`);
        if (!participantId || !type) {
            return;
        }
        const newRemoteTracks = Object.assign({}, this.remoteTracks);
        if (!newRemoteTracks[participantId]) {
            return;
        }
        delete newRemoteTracks[participantId][type];
        if (!newRemoteTracks[participantId].video && !newRemoteTracks[participantId].audio) {
            delete newRemoteTracks[participantId];
        }
        remoteTracks.set(newRemoteTracks);
    }
    onTrackMuteChanged(track) {
        var _a, _b;
        if (track.isLocal()) {
            return;
        }
        otherMicrophoneEnabled.set(!track.muted);
        if (!track.muted) {
            this.events.emit('remote_track_unmuted', {
                type: (_a = track.getType) === null || _a === void 0 ? void 0 : _a.call(track),
                participantId: (_b = track.getParticipantId) === null || _b === void 0 ? void 0 : _b.call(track),
            });
        }
    }
    async onUserJoined(id, user) {
        logger.info('User joined room', id);
        remoteLocation.set(user.getDisplayName());
        await setClientStatus(this.clientId, 'in-conversation');
    }
    async onUserLeft(id) {
        logger.info('User left room', id);
        const newRemoteTracks = Object.assign({}, this.remoteTracks);
        delete newRemoteTracks[id];
        remoteTracks.set(newRemoteTracks);
        remoteLocation.set(null);
        await setClientStatus(this.clientId, 'in-room');
    }
    onConferenceFailed(error, ...params) {
        logger.error(`Conference failed with reason: ${error}. Params: ${JSON.stringify(params)}`, new Error(error));
        if (this.intentionalDisconnect) {
            return;
        }
        this.scheduleReconnect(`conference failed: ${error}`);
    }
    onConferenceError(error, ...params) {
        logger.error(`Conference error with reason: ${error}. Params: ${JSON.stringify(params)}`, new Error(error));
    }
    onTrackStreamingStatusChanged(track, status) {
        var _a, _b, _c, _d, _e, _f;
        logger.info(`Track streaming status changed to '${status}' for ${(_a = track === null || track === void 0 ? void 0 : track.getType) === null || _a === void 0 ? void 0 : _a.call(track)} track of participant ${(_b = track === null || track === void 0 ? void 0 : track.getParticipantId) === null || _b === void 0 ? void 0 : _b.call(track)}`);
        if (status !== 'active') {
            logger.error(`Track streaming status degraded to '${status}' for ${(_c = track === null || track === void 0 ? void 0 : track.getType) === null || _c === void 0 ? void 0 : _c.call(track)} track of participant ${(_d = track === null || track === void 0 ? void 0 : track.getParticipantId) === null || _d === void 0 ? void 0 : _d.call(track)}`, new Error(`Track streaming status: ${status}`));
        }
        else {
            this.events.emit('track_streaming_active', {
                type: (_e = track === null || track === void 0 ? void 0 : track.getType) === null || _e === void 0 ? void 0 : _e.call(track),
                participantId: (_f = track === null || track === void 0 ? void 0 : track.getParticipantId) === null || _f === void 0 ? void 0 : _f.call(track),
            });
        }
    }
    onMediaConnectionInterrupted() {
        logger.error('Media (ICE) connection interrupted', new Error('Media connection interrupted'));
        this.clearIceWatchdog();
        this.iceWatchdog = window.setTimeout(() => {
            this.iceWatchdog = null;
            if (this.intentionalDisconnect)
                return;
            logger.warn('ICE did not restore in time, reconnecting signalling');
            this.scheduleReconnect('ice interrupted timeout');
        }, ICE_INTERRUPT_RECONNECT_MS);
    }
    onMediaConnectionRestored() {
        logger.info('Media (ICE) connection restored');
        this.clearIceWatchdog();
        this.updateReceiverConstraints();
        this.events.emit('media_restored');
    }
    onRingStart(e) {
        var _a;
        if (((_a = e.attributes) === null || _a === void 0 ? void 0 : _a.clientId) === this.clientId)
            return;
        this.events.emit('ring_start');
    }
    onRingStop(e) {
        var _a;
        if (((_a = e.attributes) === null || _a === void 0 ? void 0 : _a.clientId) === this.clientId)
            return;
        this.events.emit('ring_stop');
    }
    bindNetworkListeners() {
        if (this.networkListenersBound || typeof window === 'undefined')
            return;
        window.addEventListener('online', this.onBrowserOnline);
        this.networkListenersBound = true;
    }
    onBrowserOnline() {
        if (this.intentionalDisconnect)
            return;
        if (this.signalingConnected && this.conferenceJoined)
            return;
        logger.info('Browser reported online, attempting Jitsi reconnect');
        this.scheduleReconnect('browser online');
    }
    scheduleJwtRefresh() {
        this.clearJwtRefreshTimer();
        if (!this.token)
            return;
        const expMs = jwtExpiryMs(this.token);
        const delay = expMs
            ? Math.max(JWT_REFRESH_MIN_DELAY_MS, expMs - Date.now() - JWT_REFRESH_MARGIN_MS)
            : 45 * 60 * 1000;
        logger.info(`Scheduling Jitsi JWT refresh in ${Math.round(delay / 1000)}s`);
        this.jwtRefreshTimer = window.setTimeout(() => {
            this.jwtRefreshTimer = null;
            void this.refreshJwt('scheduled refresh');
        }, delay);
    }
    async refreshJwt(reason) {
        if (this.intentionalDisconnect)
            return;
        logger.info(`Refreshing Jitsi JWT (${reason})`);
        const token = await this.fetchJaasToken();
        if (!token) {
            this.scheduleReconnect('jwt refresh failed');
            return;
        }
        this.token = token;
        this.scheduleJwtRefresh();
        if (this.connection &&
            typeof this.connection.refreshToken === 'function' &&
            this.signalingConnected) {
            try {
                await this.connection.refreshToken(token);
                logger.info('Jitsi JWT refreshed in place');
                return;
            }
            catch (error) {
                logger.error('In-place JWT refresh failed, reconnecting', error);
            }
        }
        this.scheduleReconnect('jwt refresh requires reconnect');
    }
    scheduleReconnect(reason) {
        if (this.intentionalDisconnect) {
            return;
        }
        if (this.reconnecting) {
            this.reconnectQueuedReason = reason;
            logger.info(`Reconnect in progress, queued another attempt: ${reason}`);
            return;
        }
        if (this.reconnectTimer != null) {
            logger.info(`Reconnect already pending, ignoring: ${reason}`);
            return;
        }
        this.reconnectAttempt += 1;
        if (this.reconnectAttempt > MAX_RECONNECT_ATTEMPTS) {
            logger.error(`Giving up in-process reconnect after ${MAX_RECONNECT_ATTEMPTS} attempts, reloading`, new Error(reason));
            reload();
            return;
        }
        const delay = Math.min(30000, 1000 * Math.pow(2, this.reconnectAttempt - 1));
        logger.info(`Scheduling Jitsi reconnect in ${delay}ms (attempt ${this.reconnectAttempt}): ${reason}`);
        this.reconnectTimer = window.setTimeout(() => {
            this.reconnectTimer = null;
            void this.reconnect(reason);
        }, delay);
    }
    async reconnect(reason) {
        if (this.intentionalDisconnect)
            return;
        if (this.reconnecting)
            return;
        this.reconnecting = true;
        logger.info(`Reconnecting Jitsi (${reason})`);
        try {
            await this.teardown({ disposeLocalTracks: false });
            await this.initConnection();
            if (!this.connection) {
                this.scheduleReconnect('init after reconnect failed');
                return;
            }
            this.connection.connect();
        }
        catch (error) {
            logger.error('Reconnect failed', error);
            this.reconnectQueuedReason = this.reconnectQueuedReason || 'reconnect threw';
        }
        finally {
            this.reconnecting = false;
            const queued = this.reconnectQueuedReason;
            this.reconnectQueuedReason = null;
            if (queued && !this.signalingConnected) {
                this.scheduleReconnect(queued);
            }
        }
    }
    withTimeout(promise, ms) {
        return Promise.race([promise, new Promise((resolve) => window.setTimeout(resolve, ms))]);
    }
    async teardown({ disposeLocalTracks }) {
        this.intentionalDisconnect = true;
        this.clearReconnectTimer();
        this.clearIceWatchdog();
        this.clearJwtRefreshTimer();
        this.signalingConnected = false;
        this.conferenceJoined = false;
        if (this.room) {
            try {
                this.removeRoomListeners();
                const leave = this.room.leave();
                if (leave && typeof leave.then === 'function') {
                    await this.withTimeout(leave, 3000);
                }
            }
            catch (error) {
                logger.warn('Failed to leave room during teardown', error);
            }
            this.room = null;
        }
        if (this.connection) {
            try {
                this.removeConnectionListeners();
                const result = this.connection.disconnect();
                if (result && typeof result.then === 'function') {
                    await this.withTimeout(result, 3000);
                }
            }
            catch (error) {
                logger.warn('Failed to disconnect during teardown', error);
            }
            this.connection = null;
        }
        if (disposeLocalTracks) {
            this.localTracks.forEach((local) => {
                var _a, _b;
                try {
                    (_b = (_a = local.track) === null || _a === void 0 ? void 0 : _a.dispose) === null || _b === void 0 ? void 0 : _b.call(_a);
                }
                catch (error) {
                    logger.warn('Failed to dispose local track', error);
                }
            });
            this.localTracks = [];
        }
        this.intentionalDisconnect = false;
    }
    clearReconnectTimer() {
        if (this.reconnectTimer != null) {
            clearTimeout(this.reconnectTimer);
            this.reconnectTimer = null;
        }
    }
    clearIceWatchdog() {
        if (this.iceWatchdog != null) {
            clearTimeout(this.iceWatchdog);
            this.iceWatchdog = null;
        }
    }
    clearJwtRefreshTimer() {
        if (this.jwtRefreshTimer != null) {
            clearTimeout(this.jwtRefreshTimer);
            this.jwtRefreshTimer = null;
        }
    }
}
export default new JitsiService();
